import type {
  TDerivedMetricCategory,
  TDerivedMetricDto,
  TDerivedMetricExpression,
  TDerivedMetricFactTableOptionDto,
  TDerivedMetricOperand,
  TDerivedMetricOperation,
  TDerivedMetricOptionsDto,
  TDerivedMetricTexts,
  TDerivedMetricUnit,
  TRichTextDocument
} from '@homepal/api-contract'

import { Document } from '@contentful/rich-text-types'

import {
  fromContentfulDocument,
  toContentfulDocument
} from 'components_new/molecules/RichTextEditor/contentful'
import { RichText } from 'components_new/molecules/RichTextEditor/types'

/**
 * One nested expression, no more — the same limit the API enforces. The
 * editor stops offering the nested option at that depth instead of letting
 * the save fail.
 */
export const MAX_EXPRESSION_DEPTH = 2

export type MetricLocale = 'sv' | 'en'

type LocalizedInput = Record<MetricLocale, string>
type LocalizedRichTextInput = Record<MetricLocale, RichText | null>

/**
 * The CMS fields as the form holds them: numbers as the text that was typed,
 * rich text as editor values, and both languages side by side.
 */
export type MetricTextsState = {
  title: LocalizedInput
  summary: LocalizedInput
  slug: string
  subcategory_slug: string
  hidden: boolean
  body: LocalizedRichTextInput
  calculation: LocalizedRichTextInput
  purpose: LocalizedRichTextInput
}

export type MetricFormState = {
  /** Null until one is picked — there is no sensible category to guess. */
  category: TDerivedMetricCategory | null
  unit: TDerivedMetricUnit | null
  increase_is_positive: boolean
  fact_table_id: string | null
  expression: TDerivedMetricExpression
  texts: MetricTextsState
}

export const OPERATION_LABELS: Record<TDerivedMetricOperation, string> = {
  '+': '+',
  '-': '−',
  '*': '×',
  '/': '÷'
}

/**
 * What the CMS calls a category. The names come from the API, so nothing here
 * spells them out — the enum value stands in until the options have loaded.
 */
export function findCategoryName(
  options: TDerivedMetricOptionsDto,
  category: TDerivedMetricCategory | null
): string | null {
  if (!category) {
    return null
  }

  return (
    options.categories.find((item) => item.value === category)?.name ?? category
  )
}

export const CATEGORIES: TDerivedMetricCategory[] = [
  'ECONOMY',
  'ENERGY',
  'REAL_ESTATE_PORTFOLIO',
  'RENTAL',
  'TECHNICAL_MANAGEMENT',
  'HOMEPAL'
]

export const EMPTY_METRIC_OPERAND: TDerivedMetricOperand = {
  type: 'metric',
  kpi_template_id: '',
  slice_ids: []
}

export const EMPTY_EXPRESSION: TDerivedMetricExpression = {
  operation: '/',
  left: EMPTY_METRIC_OPERAND,
  right: EMPTY_METRIC_OPERAND
}

const EMPTY_LOCALIZED: LocalizedInput = { sv: '', en: '' }
const EMPTY_RICH_TEXT: LocalizedRichTextInput = { sv: null, en: null }

export const INITIAL_TEXTS_STATE: MetricTextsState = {
  title: EMPTY_LOCALIZED,
  summary: EMPTY_LOCALIZED,
  slug: '',
  subcategory_slug: '',

  // A metric built here is not part of the public library until someone says
  // so, so it starts out hidden from homepal.se.
  hidden: true,
  body: EMPTY_RICH_TEXT,
  calculation: EMPTY_RICH_TEXT,
  purpose: EMPTY_RICH_TEXT
}

export const INITIAL_FORM_STATE: MetricFormState = {
  category: null,
  unit: null,
  increase_is_positive: true,
  fact_table_id: null,
  expression: EMPTY_EXPRESSION,
  texts: INITIAL_TEXTS_STATE
}

/**
 * A slug from a title, so nobody has to invent one by hand. The transliteration
 * is a safety net: the slug is built from the English title, which should not
 * carry å, ä or ö in the first place.
 */
export function toSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[åä]/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * The address on homepal.se the English name would produce. The address is
 * English wherever it appears, so the Swedish name never stands in for it.
 */
export function deriveSlug(texts: MetricTextsState): string {
  return toSlug(texts.title.en)
}

/**
 * Texts with one of the names changed, and the slug following the English one
 * along unless it has been written by hand — a published metric should not
 * change address because someone corrected a typo in its name.
 */
export function withTitle(
  texts: MetricTextsState,
  locale: MetricLocale,
  value: string
): MetricTextsState {
  const next = { ...texts, title: { ...texts.title, [locale]: value } }

  if (locale !== 'en') {
    return next
  }

  return {
    ...next,
    slug: texts.slug === deriveSlug(texts) ? deriveSlug(next) : texts.slug
  }
}

/**
 * The wire keeps a rich text document opaque, so the two representations meet
 * here: identical in shape, but `nodeType` is a plain string on the wire and
 * an enum member in Contentful's own types.
 */
function asDocument(
  document: TRichTextDocument | null | undefined
): Document | null {
  return (document as Document | null | undefined) ?? null
}

function asWireDocument(document: Document | null): TRichTextDocument | null {
  return document as TRichTextDocument | null
}

function toRichTextState(
  field: TDerivedMetricTexts['body']
): LocalizedRichTextInput {
  return {
    sv: fromContentfulDocument(asDocument(field.sv)),
    en: field.en ? fromContentfulDocument(asDocument(field.en)) : null
  }
}

/**
 * The stored texts as form state.
 *
 * @param texts - CMS entry, or null for a metric that has none yet.
 * @param name - Metric name, which stands in for a missing title.
 */
export function toTextsState(
  texts: TDerivedMetricTexts | null,
  name: string
): MetricTextsState {
  // No entry yet: the name is the Swedish one, and the slug waits for an
  // English name rather than being transliterated from Swedish.
  if (!texts) {
    return { ...INITIAL_TEXTS_STATE, title: { sv: name, en: '' } }
  }

  return {
    title: { sv: texts.title.sv, en: texts.title.en ?? '' },
    summary: { sv: texts.summary.sv, en: texts.summary.en ?? '' },
    slug: texts.slug,
    subcategory_slug: texts.subcategory_slug,
    hidden: texts.hidden,
    body: toRichTextState(texts.body),
    calculation: toRichTextState(texts.calculation),
    purpose: toRichTextState(texts.purpose)
  }
}

export function toFormState(metric: TDerivedMetricDto): MetricFormState {
  return {
    category: metric.category,
    unit: metric.unit,
    increase_is_positive: metric.increase_is_positive,
    fact_table_id: metric.fact_table_id,
    expression: metric.expression,
    texts: toTextsState(metric.texts, metric.name)
  }
}

function toRichTextBody(
  field: LocalizedRichTextInput
): TDerivedMetricTexts['body'] {
  return {
    sv: asWireDocument(toContentfulDocument(field.sv)),
    en: asWireDocument(toContentfulDocument(field.en))
  }
}

/**
 * Form state as the CMS fields the API expects. An empty English text is left
 * out rather than stored as an empty string — the CMS falls back to Swedish.
 */
export function toTextsBody(state: MetricTextsState): TDerivedMetricTexts {
  return {
    title: { sv: state.title.sv.trim(), en: state.title.en.trim() || null },
    summary: {
      sv: state.summary.sv.trim(),
      en: state.summary.en.trim() || null
    },
    slug: state.slug.trim(),
    subcategory_slug: state.subcategory_slug,
    hidden: state.hidden,

    // The featured shelf on homepal.se is picked in Contentful, on the rare
    // occasion anyone wants a derived metric on it.
    featured: false,
    body: toRichTextBody(state.body),
    calculation: toRichTextBody(state.calculation),
    purpose: toRichTextBody(state.purpose)
  }
}

/** A field a metric cannot be saved without. */
export type MetricField =
  | 'fact_table'
  | 'expression'
  | 'category'
  | 'subcategory'
  | 'title_sv'
  | 'title_en'
  | 'summary_sv'
  | 'summary_en'
  | 'slug'

export const FIELD_LABELS: Record<MetricField, string> = {
  fact_table: 'Faktatabell',
  expression: 'Uttryck',
  category: 'Kategori',
  subcategory: 'Underkategori',
  title_sv: 'Namn [SE]',
  title_en: 'Namn [EN]',
  summary_sv: 'Kort beskrivning [SE]',
  summary_en: 'Kort beskrivning [EN]',
  slug: 'Slug'
}

/**
 * The fields that are still empty, in the order they appear in the form, so
 * the save button can say what it is waiting for and the rows can mark
 * themselves.
 *
 * Both names are required: the English one is the address on homepal.se as
 * well as the title an English reader sees.
 */
export function missingFields(state: MetricFormState): MetricField[] {
  const missing: MetricField[] = []

  if (!state.fact_table_id) {
    missing.push('fact_table')
  }

  if (!isCompleteExpression(state.expression)) {
    missing.push('expression')
  }

  if (!state.category) {
    missing.push('category')
  }

  if (!state.texts.subcategory_slug) {
    missing.push('subcategory')
  }

  if (!state.texts.title.sv.trim()) {
    missing.push('title_sv')
  }

  if (!state.texts.title.en.trim()) {
    missing.push('title_en')
  }

  if (!state.texts.summary.sv.trim()) {
    missing.push('summary_sv')
  }

  if (!state.texts.summary.en.trim()) {
    missing.push('summary_en')
  }

  // Follows the English name on its own, but can be emptied by hand.
  if (!state.texts.slug.trim()) {
    missing.push('slug')
  }

  return missing
}

/**
 * Why a metric cannot be saved yet, as one sentence.
 */
export function missingFieldsSentence(missing: MetricField[]): string {
  const labels = missing.map((field) => FIELD_LABELS[field])

  return `Fyll i ${labels.join(', ')} för att kunna spara mätetalet.`
}

/** The id the form gives a field's row, so it can be scrolled to. */
export function fieldRowId(field: MetricField): string {
  return `metric-field-${field}`
}

function operandLabel(
  operand: TDerivedMetricOperand,
  factTable: TDerivedMetricFactTableOptionDto | null
): string {
  if (operand.type === 'constant') {
    return `${operand.value}`
  }

  if (operand.type === 'expression') {
    return `(${formatExpression(operand.expression, factTable)})`
  }

  const metric = factTable?.metrics.find(
    (item) => item.id === operand.kpi_template_id
  )

  const name = metric?.name ?? 'mätetal'

  if (operand.slice_ids.length === 0) {
    return name
  }

  const sliceNames = operand.slice_ids.map(
    (id) =>
      factTable?.slices.find((slice) => slice.id === id)?.name ?? 'skärning'
  )

  return `${name} [${sliceNames.join(', ')}]`
}

/**
 * The definition as one readable line, e.g.
 * `Utfall [Vidaredebitering uppvärmning] ÷ (Utfall [kostnad] × -1)`.
 */
export function formatExpression(
  expression: TDerivedMetricExpression,
  factTable: TDerivedMetricFactTableOptionDto | null
): string {
  const operation = OPERATION_LABELS[expression.operation]

  return [
    operandLabel(expression.left, factTable),
    operation,
    operandLabel(expression.right, factTable)
  ].join(' ')
}

function operandIsComplete(operand: TDerivedMetricOperand): boolean {
  if (operand.type === 'metric') {
    return !!operand.kpi_template_id
  }

  if (operand.type === 'constant') {
    return Number.isFinite(operand.value)
  }

  return isCompleteExpression(operand.expression)
}

export function isCompleteExpression(
  expression: TDerivedMetricExpression
): boolean {
  return (
    operandIsComplete(expression.left) && operandIsComplete(expression.right)
  )
}

function operandMetricIds(operand: TDerivedMetricOperand): string[] {
  if (operand.type === 'metric') {
    return operand.kpi_template_id ? [operand.kpi_template_id] : []
  }

  if (operand.type === 'expression') {
    return expressionMetricIds(operand.expression)
  }

  return []
}

export function expressionMetricIds(
  expression: TDerivedMetricExpression
): string[] {
  return [
    ...operandMetricIds(expression.left),
    ...operandMetricIds(expression.right)
  ]
}

/**
 * The fact table a saved metric belongs to, for rendering its formula.
 */
export function findFactTable(
  options: TDerivedMetricFactTableOptionDto[],
  factTableId: string | null
): TDerivedMetricFactTableOptionDto | null {
  return options.find((option) => option.id === factTableId) ?? null
}

/**
 * The whole definition as one line, the way it reads in the formula box:
 * `Namn = Utfall (Debiterad uppvärmning) ÷ (Utfall (Uppvärmningskostnader)
 * × -1) × 100`.
 *
 * The trailing × 100 is not part of the stored expression — percentages are
 * scaled by the pipeline — but leaving it out makes the formula look like it
 * yields a fraction.
 */
export function formatFormula(
  name: string,
  expression: TDerivedMetricExpression,
  factTable: TDerivedMetricFactTableOptionDto | null,
  unit: TDerivedMetricUnit | null
): string {
  const body = formatExpression(expression, factTable)

  const scale = unit === '%' ? ' × 100' : ''

  return `${name || 'Nytt mätetal'} = ${body}${scale}`
}
